/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/ban-types */
import '../_debug_hook';
import type { Disposer } from '@visiba/std';
import { FixedSizeImpl } from '../data_struct/fixed_size_impl';
import { PRESENTATION_DEBUG_REF } from './common';

/**
 * @deprecated Represent loading/error/access states with React state/context, custom hooks, or TanStack Query status instead.
 */
export enum Flow {
	ACCESSIBLE,
	UNSET,
	PENDING,
	ERROR,
}

interface Specification<T> {
	initialValue?: T;
	designatedFlowState?: Flow;
}

/**
 * ## cortex:presentation:FlowPresentation
 *
 * @public
 * @deprecated Use React state/context and custom hooks for UI state, or TanStack Query for loading/error server state.
 */
export class FlowPresentation<T extends object> {
	public error: Error | null = null;
	public flowState: Readonly<Flow> = Flow.UNSET;
	private data: FixedSizeImpl<T> | null = null;
	private readonly hooks: Map<symbol, (changeSet?: Set<keyof T>) => void> = new Map();

	constructor(spec: Specification<T> = {}) {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		//@ts-ignore
		if (globalThis[PRESENTATION_DEBUG_REF]) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			//@ts-ignore
			globalThis[PRESENTATION_DEBUG_REF].add(this);
		}

		const { initialValue, designatedFlowState } = spec;

		if (designatedFlowState !== undefined) this.flowState = designatedFlowState;

		if (initialValue) {
			this.data = new FixedSizeImpl(initialValue);
		}
	}

	public subscribe(event: (changeSet?: Set<keyof T>) => void): Disposer {
		const id = Symbol();

		this.hooks.set(id, event);

		return () => {
			this.hooks.delete(id);
		};
	}

	public unsafeWrite(currentState: Partial<T> | ((currentState: Readonly<Omit<FixedSizeImpl<T>, 'insert'>>) => Partial<T>)): void {
		if (!this.data) throw new Error('data need to first be set');

		try {
			const newState = currentState instanceof Function ? currentState(this.data) : currentState;

			const changeSet = this.data.insert(newState);
			this.error = null;
			this.flowState = Flow.ACCESSIBLE;
			this.dispatch(changeSet);
		} catch (error) {
			this.error = new Error(`could not mutate the state:\n\n${error}`);
			this.flowState = Flow.ERROR;
			this.dispatch();
		}
	}

	public read(): [data: Readonly<T>, flow: Flow] {
		// The following is purposely being ignored by TypeScript, when the state is
		// not accessible it should deliberately cause an error so a correct
		// Behavior can be written
		switch (this.flowState) {
			case Flow.PENDING:
			case Flow.UNSET:
			case Flow.ERROR:
				// @ts-ignore
				return [undefined, this.flowState];
			default: {
				if (!this.data) {
					this.flowState = Flow.UNSET;

					// @ts-ignore
					return [this.flowState, Flow.UNSET];
				}

				return [this.data.extract(), this.flowState];
			}
		}
	}

	public peekState(): Readonly<T> {
		const [state] = this.read();

		return state;
	}

	public changeFlowTo(action: Flow): void {
		this.flowState = action;
		this.dispatch();
	}

	public overwriteData(state: T): void {
		this.data = new FixedSizeImpl(state);
	}

	private dispatch(changeSet?: Set<keyof T>): void {
		this.hooks.forEach((hook) => hook(changeSet));
	}
}
